import hyperid from 'hyperid';
import { chunk, has, sum, isEmpty, flatMap, takeRight, get, set, zipWith, last, chain } from 'lodash';
import cartesian from 'cartesian';
import { NodeType, GateType, TriggerType } from './types/common';
export var AnnotationType;
(function (AnnotationType) {
    AnnotationType[AnnotationType["dataTrigger"] = 0] = "dataTrigger";
    AnnotationType[AnnotationType["tickTrigger"] = 1] = "tickTrigger";
    AnnotationType[AnnotationType["loopDef"] = 2] = "loopDef";
})(AnnotationType || (AnnotationType = {}));
const DEFAULT_ANNOTATIONS_MAP = {
    [AnnotationType.dataTrigger]: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@v12.1.4/assets/72x72/26a1.png',
    [AnnotationType.tickTrigger]: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@v12.1.4/assets/72x72/23f1.png',
    [AnnotationType.loopDef]: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@12.1.4/assets/72x72/27b0.png'
};
const generateId = hyperid();
// TODO deprecate this function
const createDecoder = decode;
/**
 * Convert Waylay BN to Graph Editor network
 *
 * Belief network nodes are sensors and gates
 * Actions and parents / children are edges
 */
function decode(network, options = {}) {
    const { plugCache = [], annotationsMap } = options;
    const icons = plugCache.reduce((acc, plug) => {
        return set(acc, [plug.type, plug.name], plug.iconURL);
    }, {});
    if (!network.nodes) {
        Object.assign(network, { nodes: [] });
    }
    const nodes = flatMap(network.nodes, (node) => {
        const type = nodeType(network, node);
        if (type === NodeType.SENSOR) {
            return createSensorNode(node);
        }
        if (type === NodeType.ACTUATOR) {
            return createActuatorNode(node);
        }
        return createGateNode(network, node);
    });
    // we create edges by iterating over the "nodes" and connecting them with each "child"
    const edges = network.nodes.reduce((acc, node) => {
        const edges = createEdges(node);
        return edges.length > 0 ? acc.concat(edges) : acc;
    }, []);
    // these are notes you can drop into the graph editor
    const notes = get(network, 'metadata.infoBoxes', []).map((infoBox) => {
        const { x, y, text } = infoBox;
        return {
            group: 'nodes',
            position: { x, y },
            data: {
                type: NodeType.NOTE,
                label: text
            }
        };
    });
    return {
        name: network.name,
        elements: [...nodes, ...edges, ...notes]
    };
    function createEdges(node) {
        const { name, properties } = node;
        const { posterior = [] } = network;
        const { actions = [], nodeTrigger } = properties;
        let edges = []; // collect edges in this array
        /**
         * Let's first discover graph items from connected actuators,
         * called "actions" in the BN network
         *
         * An action can be connected via state change (nodeTrigger) or
         * via direct state connection (triggeredStates) – you can have both!
         */
        const actionItems = actions.reduce((acc = [], action) => {
            const hasStateChange = has(action, ['nodeTrigger', 'stateFrom']) &&
                has(action, ['nodeTrigger', 'stateTo']);
            const hasTriggeredStates = !isEmpty(get(action, 'triggeredStates'));
            if (hasStateChange) {
                // this will create three additional graph items (one node and two edges)
                const nodes = createMemoryBlocksFromAction(node, action);
                acc = acc.concat(...nodes);
            }
            if (hasTriggeredStates) {
                const edge = createEdge(name, action.label, {
                    triggerType: TriggerType.STATES,
                    states: action.triggeredStates
                });
                acc.push(edge);
            }
            return acc;
        }, []);
        edges = edges.concat(actionItems);
        /**
         * Let's continue to figure out what other edges need to be drawn from this node
         *
         * These nodes are a bit different than the actions; there is always a 'nodeTrigger' property
         * however this 'nodeTrigger' property contains both 'states', 'stateFrom' and 'stateTo'.
         *
         * If we have both 'stateFrom' and 'stateTo' there is a stateChange configured,
         * if we have a states array there are triggered states – you can have both!
         *
         * A node can also have an "always" configured,
         * this is exclusive of state changes and connected states.
         */
        if (nodeTrigger) {
            const { stateFrom, stateTo, states } = nodeTrigger;
            const hasStateChange = Boolean(stateFrom && stateTo);
            const hasStates = has(nodeTrigger, 'states');
            const isAlways = !stateFrom && !stateTo && !states;
            if (isAlways) {
                const edge = createEdge(nodeTrigger.nodeName, node.name, {
                    triggerType: TriggerType.ALWAYS
                });
                edges.push(edge);
            }
            // create memory block and edges
            if (hasStateChange) {
                const sourceNode = findNodeByLabel(nodeTrigger.nodeName);
                const targetNode = findNodeByLabel(node.name);
                const items = createMemoryBlocksFromNodes(sourceNode, targetNode, stateFrom, stateTo);
                items.forEach((i) => edges.push(i));
            }
            if (hasStates) {
                const edge = createEdge(nodeTrigger.nodeName, node.name, {
                    triggerType: TriggerType.STATES,
                    states
                });
                edges.push(edge);
            }
        }
        // find posteriors where we are the target
        posterior
            .filter((p) => p.nodes[0] === node.name)
            .forEach((posterior) => {
            const { nodes } = posterior;
            const sourceLabels = takeRight(nodes, nodes.length - 1);
            sourceLabels.forEach((sourceLabel) => {
                const sourceNode = findNodeByLabel(sourceLabel);
                const edgeProps = getStatesFromChild(node.name, sourceNode);
                const edge = createEdge(sourceLabel, node.name, edgeProps);
                edges.push(edge);
            });
        });
        function createEdge(sourceLabel, targetLabel, edgeProps) {
            const id = generateId();
            const targetNode = findNodeByLabel(targetLabel);
            const target = targetNode.data.id;
            const sourceNode = findNodeByLabel(sourceLabel);
            const source = sourceNode.data.id;
            return {
                group: 'edges',
                data: Object.assign({ id,
                    source,
                    target }, edgeProps)
            };
        }
        function createMemoryBlocksFromAction(source, target) {
            const { stateFrom, stateTo } = target.nodeTrigger;
            const sourceNode = findNodeByLabel(source.name);
            const targetNode = findNodeByLabel(target.label);
            return createMemoryBlocksFromNodes(sourceNode, targetNode, stateFrom, stateTo);
        }
        return edges;
    }
    /**
     * Turn a stateChange connection into a separate "memory block" and connect it
     * with the original source and target nodes
     */
    function createMemoryBlocksFromNodes(sourceNode, targetNode, stateFrom, stateTo) {
        const memoryBlockId = generateId();
        const starToStar = stateFrom === '*' && stateTo === '*';
        const sourceEdge = {
            group: 'edges',
            data: {
                id: generateId(),
                source: sourceNode.data.id,
                target: memoryBlockId,
                triggerType: TriggerType.STATE_CHANGE_DECISION,
                // not entirely sure why but for * -> * we have to return
                // undefined for the graph editor to draw it properly
                states: starToStar ? undefined : [stateFrom, stateTo]
            }
        };
        const targetEdge = {
            group: 'edges',
            data: {
                id: generateId(),
                source: memoryBlockId,
                target: targetNode.data.id
            }
        };
        const memoryBlock = {
            group: 'nodes',
            data: {
                id: memoryBlockId,
                label: `${stateFrom} → ${stateTo}`,
                type: NodeType.MEMORY_BLOCK,
                stateFrom,
                stateTo,
                sourceId: sourceNode.data.id,
                targetId: targetNode.data.id
            }
        };
        return [sourceEdge, memoryBlock, targetEdge];
    }
    // if our target node is a gate, we need to add the state(s) to the edge
    function getStatesFromChild(targetLabel, sourceNode) {
        const targetNode = findNodeByLabel(targetLabel);
        if (targetNode.data.type === NodeType.GATE) {
            const states = getConnectedStateFromPosterior(targetNode, sourceNode);
            return { triggerType: TriggerType.STATES, states };
        }
        // TODO find connected states in sensor to sensor connection
        return { triggerType: TriggerType.STATES, states: ['*'] };
    }
    function getConnectedStateFromPosterior(targetNode, sourceNode) {
        const posterior = network.posterior;
        const myPosterior = findPosteriorByNodeName(posterior, targetNode.data.label);
        // ⚠ do not count gate node itself ⚠
        const posteriorNodes = takeRight(myPosterior.nodes.map(findNetworkNodeByName), myPosterior.nodes.length - 1);
        const isGate = get(targetNode, 'data.type') === 'gate';
        const gateType = get(targetNode, 'data.gateType');
        const isNANDGate = isGate && gateType === 'NAND';
        // we are splitting up the posterior in two chunks, called 'left' and 'right'
        const [left, right] = chunk(myPosterior.function, myPosterior.function.length / 2);
        /**
         * For the NAND (old OR) gate we look in the second half of the posterior,
         * it is a mirrored version of the first half – and find truthy indexes in that one.
         *
         * This is similar to grabbing the 0 index of the first half for AND gates
         * and the 1 index of the first half for NAND (old OR) gates.
         *
         * See https://github.com/waylayio/WaylayDashboard/blob/18de85c6c785ef08636ecc695453b85da88a503f/js/services/bn.js#L491-L494
         */
        const relevantIndexes = isNANDGate ? right : left;
        const truthyIndexes = relevantIndexes.reduce((acc, n, index) => {
            return n === 1 ? acc.concat(index) : acc;
        }, []);
        const possibleStates = posteriorNodes.map((n) => n.states);
        const cartesianProduct = cartesian(possibleStates);
        const sourceIndex = posteriorNodes.findIndex((n) => n.name === sourceNode.data.label);
        const indexRow = truthyIndexes.map((index) => {
            return cartesianProduct[index];
        });
        const sourceIndexRow = indexRow.map((r) => r[sourceIndex]);
        // we only have to return the state that occurs the most in indexRow
        const isORGate = isGate && gateType === 'OR';
        if (isORGate) {
            const stateCounts = sourceIndexRow.reduce((stateCounts, state) => {
                stateCounts[state] = (stateCounts[state] || 0) + 1;
                return stateCounts;
            }, {});
            const mostOccurringState = Object.entries(stateCounts).reduce((maxState, nextState) => {
                const maxOcc = maxState[1] || 0;
                const nextOcc = nextState[1];
                if (nextOcc > maxOcc)
                    maxState = nextState;
                return maxState;
            }, []);
            return [mostOccurringState[0]];
        }
        return sourceIndexRow;
    }
    /**
     * This function will find a Graph Node by the label (also known as name)
     */
    function findNodeByLabel(label) {
        return nodes.find((n) => n.data.label === label);
    }
    /**
     * A node can be either a sensor, a gate or an actuator.
     * If the node has "properties.sensor" it is a sensor.
     * If the node has "actions" but no "properties.sensor" it is an actuator.
     * If none of those are true it is a gate.
     */
    function nodeType(network, node) {
        const hasSensor = node.properties.sensor;
        const hasActions = node.properties.actions;
        const posterior = network.posterior || [];
        if (hasSensor) {
            return NodeType.SENSOR;
        }
        if (hasActions &&
            !hasSensor &&
            !findPosteriorByNodeName(posterior, node.name)) {
            return NodeType.ACTUATOR;
        }
        return NodeType.GATE;
    }
    function createSensorNode(node) {
        const id = generateId();
        const { name, states, properties } = node;
        const { actions = [], sensor, position, dataTrigger, tickTrigger, loopDef, resource } = properties;
        const description = `${sensor.name} v${sensor.version}`;
        const [x, y] = position || [0, 0];
        const annotations = [];
        const labels = [];
        const getAnnotation = (type) => (annotationsMap || DEFAULT_ANNOTATIONS_MAP)[type];
        if (dataTrigger) {
            annotations.push(getAnnotation(AnnotationType.dataTrigger));
        }
        if (tickTrigger) {
            annotations.push(getAnnotation(AnnotationType.tickTrigger));
        }
        if (loopDef) {
            annotations.push(getAnnotation(AnnotationType.loopDef));
        }
        if (resource) {
            labels.push(resource);
        }
        const sensorConfiguration = getSensorConfiguration(node);
        const graphNode = {
            group: 'nodes',
            data: {
                id,
                icon: findIcon('sensor', sensor),
                type: NodeType.SENSOR,
                label: name,
                description,
                plug: {
                    name: sensor.name,
                    version: sensor.version
                },
                configuration: Object.assign({ properties: sensor.requiredProperties &&
                        propsToObject(sensor.requiredProperties) }, sensorConfiguration),
                states,
                annotations,
                labels
            },
            position: { x, y }
        };
        if (!actions) {
            return [graphNode];
        }
        const actionNodes = actions.map(createActionNode);
        return [graphNode, ...actionNodes];
    }
    function createActuatorNode(node) {
        // the position is only available on the root node so we have to copy it over to
        // be compatible with the createActionNode fn
        node.properties.actions = node.properties.actions.map((action) => (Object.assign(Object.assign({}, action), { position: node.properties.position })));
        return node.properties.actions.map(createActionNode);
    }
    function createActionNode(action) {
        const id = generateId();
        const { name, version, label, position, requiredProperties, actionPolicy } = action;
        const [x, y] = position || [0, 0];
        const description = `${name} v${version}`;
        // TODO use "actionPolicy" to create annotations
        const annotations = [];
        const properties = propsToObject(requiredProperties);
        const node = {
            group: 'nodes',
            data: {
                id,
                icon: findIcon('actuator', action),
                type: NodeType.ACTUATOR,
                label,
                description,
                configuration: {
                    properties,
                    policy: actionPolicy
                },
                plug: {
                    name,
                    version
                },
                annotations
            },
            position: { x, y }
        };
        return node;
    }
    function createGateNode(network, node) {
        const id = generateId();
        const gateType = posteriorToGateType(network, node);
        const { name, properties: { position, actions = [] } } = node;
        const [x, y] = position || [0, 0];
        const graphNode = {
            group: 'nodes',
            data: {
                id,
                label: name,
                type: NodeType.GATE,
                gateType
            },
            position: { x, y }
        };
        const actionNodes = actions.map(createActionNode);
        return [graphNode, ...actionNodes];
    }
    function findIcon(type, plug) {
        const { name } = plug;
        const iconURL = get(icons, [type, name]);
        return iconURL ? String(iconURL) : undefined;
    }
    function posteriorToGateType(network, node) {
        const posteriors = network.posterior;
        const myPosterior = findPosteriorByNodeName(posteriors, node.name);
        const numFunctions = myPosterior.function.length;
        const [firstHalf] = chunk(myPosterior.function, numFunctions / 2);
        const sumFirstHalf = sum(firstHalf);
        if (sumFirstHalf === 1) {
            return GateType.AND;
        }
        else if (sumFirstHalf === numFunctions / 2 - 1) {
            // Resolve collision between NAND and OR gate nodes using node name
            return node.name.startsWith(GateType.NAND) ? GateType.NAND : GateType.OR;
        }
        else {
            const parentStates = getParentsFromPosterior(node.name).map((node) => node.states);
            if (checkForOrGate(parentStates, firstHalf)) {
                // Resolve collision between GENERAL and OR gate nodes using node name
                return node.name.startsWith(GateType.GENERAL)
                    ? GateType.GENERAL
                    : GateType.OR;
            }
        }
        return GateType.GENERAL;
    }
    function checkForOrGate(states, posteriorFunction) {
        const stateCombinations = cartesian(states);
        const numberOfStateCombinations = stateCombinations.length;
        const isOrGate = chain(stateCombinations)
            // add posterior/gate output to stateCombinations
            .zipWith(posteriorFunction, (a = [], b) => [].concat(a, b))
            // we only need the combinations where gate output (last element) is 1 / true
            .filter((values) => last(values) === 1)
            // get per node all states where gate is true
            .reduce((result, current) => zipWith(result, current, (a = [], b) => [].concat(a, b)), [])
            // remove last column (includes gate output which are all true and we no longer need)
            .slice(0, -1)
            // count per node the number of time a state resulted in a true output
            .map((nodeTrueStates) => nodeTrueStates.reduce((stateCounts, state) => {
            stateCounts[state] = (stateCounts[state] || 0) + 1;
            return stateCounts;
        }, {}))
            // determine per node the state with most true output occurrences
            .map((nodeStateCounts) => {
            return Object.entries(nodeStateCounts).reduce((maxState, nextState) => {
                const maxOcc = maxState[1] || 0;
                const nextOcc = nextState[1];
                if (nextOcc > maxOcc)
                    maxState = nextState;
                return maxState;
            }, []);
        })
            /*
             * check per node that the most occurring state has the correct number of
             * occurrences with respect to the numberOfStateCombinations
             *
             * e.g. - 1 for a sensor with 6 states (e.g. dice), the most occurring state
             * should have 1/6 of the numberOfStateCombinations
             * e.g. - 2 for a sensor with 9 states (e.g. weatherSensor) the most
             * occurring state should have 1/9 of the numberOfStateCombinations
             */
            .every((state, index) => {
            const numberOfOccurrences = last(state);
            const numberOfNodeStates = states[index].length;
            const expectedTruePerState = numberOfStateCombinations / numberOfNodeStates;
            return expectedTruePerState === numberOfOccurrences;
        })
            .value();
        return isOrGate;
    }
    function findPosteriorByNodeName(posteriors, name) {
        return posteriors.find((p) => p.nodes[0] === name);
    }
    function findNetworkNodeByName(name) {
        return network.nodes.find((node) => node.name === name);
    }
    function getSensorConfiguration(node) {
        const networkNode = network.nodes.find((n) => n.name === node.name);
        const { properties } = networkNode;
        const timeout = get(properties, 'sensor.timeout');
        const timeoutState = get(properties, 'sensor.timeoutState');
        const props = pick(properties, [
            'cost',
            'resource',
            'tickTrigger',
            'dataTrigger',
            'loopDef',
            'pollingPeriod',
            'evictionTime'
        ]);
        return Object.assign(Object.assign(Object.assign({}, props), (timeout && { timeout })), (timeoutState && { timeoutState }));
    }
    function propsToObject(requiredProperties) {
        return requiredProperties.reduce((acc, property) => {
            const [k, v] = Object.entries(property)[0];
            acc[k] = v;
            return acc;
        }, {});
    }
    function getParentsFromPosterior(name) {
        const posteriors = network.posterior;
        const posterior = findPosteriorByNodeName(posteriors, name);
        return get(posterior, 'nodes', [])
            .slice(1) // remove first element as it is the gate name
            .map(findNetworkNodeByName);
    }
}
// very simple pick function
function pick(obj, props) {
    const o = {};
    for (const key of props) {
        o[key] = obj[key];
    }
    return o;
}
export default decode;
export { createDecoder };
//# sourceMappingURL=decode.js.map